import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import Spinner from '@atlaskit/spinner/spinner';
import Textfield from '@atlaskit/textfield/text-field';
import Button from '@atlaskit/button/default/button';
import Form from '@atlaskit/form/form';

import { ADVANCED_SEARCH } from '@confluence/named-routes/entry-points/routes.ts';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';
import { GenericErrorBoundary } from '@confluence/error-boundary/entry-points/GenericErrorBoundary.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import type { ExtensionHandlerProps } from '@confluence/fabric-extension-lib/entry-points/commonExtensionTypes.ts';
import type { NestedAdfRendererWrapper } from '@confluence/macro-utils/entry-points/nestedAdfRenderer.ts';
import {
	getExperienceName,
	getMacroAttributesFromADFNode,
} from '@confluence/macro-tracker/entry-points/MacroExperienceTracker.ts';
import { MacroExperienceFailure } from '@confluence/macro-tracker/entry-points/MacroExperienceFailure.ts';
import { MacroExperienceSuccess } from '@confluence/macro-tracker/entry-points/MacroExperienceSuccess.ts';

import { usePageTreeData } from './usePageTreeData.ts';
import { parseMacroParams, serializeMacroParams } from './pageTreeUtils.ts';
import { PageTreeNodeComponent } from './PageTreeNode.tsx';
import { i18n } from './pagetreeMacroMessages.ts';
import { useExcerptBatchLoader } from './useExcerptBatchLoader.ts';

type MacroParamValue = { value?: string };

export type PageTreeMacroParams = {
	root?: MacroParamValue;
	spaces?: MacroParamValue;
	excerpt?: MacroParamValue;
	sort?: MacroParamValue;
	reverse?: MacroParamValue;
	searchBox?: MacroParamValue;
	expandCollapseAll?: MacroParamValue;
	startDepth?: MacroParamValue;
	expandCurrent?: MacroParamValue;
};

export type PageTreeMacroRendererProps = {
	macroDefaultProps: ExtensionHandlerProps;
	macroParams?: PageTreeMacroParams;
	RendererWrapper: NestedAdfRendererWrapper;
};

const useParsedMacroParams = (
	macroParams: PageTreeMacroParams | undefined,
	defaultSpaceKey: string,
) => {
	const parsedParams = parseMacroParams(macroParams, defaultSpaceKey);

	const jsonParams = useMemo(() => serializeMacroParams(parsedParams), [parsedParams]);

	return { parsedParams, jsonParams };
};

const styles = cssMap({
	controls: {
		paddingBlockEnd: token('space.100'),
	},
	error: {
		padding: token('space.200'),
	},
	searchInput: {
		width: '158px',
		marginRight: token('space.100'),
		marginBottom: token('space.100'),
	},
	linkButton: {
		backgroundColor: 'transparent',
		borderStyle: 'none',
		paddingBlock: token('space.0'),
		paddingInline: token('space.0'),
		color: token('color.link'),
		cursor: 'pointer',
		font: token('font.body.large'),
	},
});

const PageTreeMacroRendererComponent = ({
	macroDefaultProps,
	macroParams,
	RendererWrapper,
}: PageTreeMacroRendererProps) => {
	const { formatMessage } = useIntl();
	const { contentId, extensionKey, mode, node } = macroDefaultProps;
	const attributes = macroDefaultProps.attributes ?? getMacroAttributesFromADFNode(node);
	const experienceName = getExperienceName(mode, node);

	const { parsedParams, jsonParams } = useParsedMacroParams(
		macroParams,
		macroDefaultProps.spaceKey ?? '',
	);
	const {
		rootParam,
		spaceKey,
		showExcerpt,
		sort,
		reverse,
		showSearchBox,
		showExpandCollapseAll,
		startDepth,
	} = parsedParams;

	const isServer = useRenderServerPlaceholder();

	const [searchQuery, setSearchQuery] = useState('');
	const [expandAllCounter, setExpandAllCounter] = useState(0);
	const [collapseAllCounter, setCollapseAllCounter] = useState(0);

	let effectiveStartDepth = startDepth;
	if (expandAllCounter > 0) {
		effectiveStartDepth = 999;
	} else if (collapseAllCounter > 0) {
		effectiveStartDepth = 0;
	}

	const { nodes, ancestorIds, loading, error, rootResolved, rootId } = usePageTreeData({
		contentId: macroDefaultProps.contentId,
		rootParam,
		spaceKey,
		sort,
		reverse,
	});

	// Single-state excerpt cache + paginated batch loader. `excerptMap` is a
	// new reference on every batch resolution.
	// `enqueueExcerptIds` is a stable callback. The instrumentation
	// in `NestedAdfRendererWrapper` (when enabled) shows whether the
	// resulting downstream re-renders are cheap.
	const { excerptMap, enqueueExcerptIds } = useExcerptBatchLoader(showExcerpt);

	// Feed visible root IDs into the loader. Auto-expanded descendants are
	// enqueued by PageTreeNodeComponent when their auto-expand children
	// arrive.
	useEffect(() => {
		if (!showExcerpt || !nodes || nodes.length === 0) return;
		enqueueExcerptIds(nodes.map((n) => n.id));
	}, [showExcerpt, nodes, enqueueExcerptIds]);

	const handleExpandAll = useCallback(() => {
		setExpandAllCounter((prev) => prev + 1);
		setCollapseAllCounter(0);
	}, []);

	const handleCollapseAll = useCallback(() => {
		setCollapseAllCounter((prev) => prev + 1);
		setExpandAllCounter(0);
	}, []);

	const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSearchQuery(event.target.value);
	};

	const handleSearchSubmit = useCallback(() => {
		const trimmed = searchQuery.trim();
		if (!trimmed) {
			return;
		}
		// Scope the search to the resolved root via the "ancestor" param, matching
		// the legacy macro and the pagetree-search-macro pattern. `rootId` is the
		// current page for @self, the homepage for @home, the parent for @parent,
		// the matched page for a title root, and `undefined` for @none (no ancestor).
		// `spaceKey` is always set here — the component early-returns above when it
		// is missing — so it can be added unconditionally.
		const queryParams: Record<string, string> = { text: trimmed, spaces: spaceKey };
		if (rootId) {
			queryParams.ancestor = rootId;
		}
		// Browser-only navigation, guarded by the SSR placeholder hook (`isServer`)
		// per the Confluence SSR Must rule rather than a raw `typeof window` check.
		// The staged guard-browser-globals rule only recognises a literal `typeof
		// window` guard, so it is disabled for this line.
		if (!isServer) {
			// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals -- guarded by useRenderServerPlaceholder (isServer) per SSR Must rule
			window.location.href = ADVANCED_SEARCH.toUrl({}, { query: queryParams });
		}
	}, [searchQuery, spaceKey, rootId, isServer]);

	if (!spaceKey) {
		const unsupportedContentError = new Error(formatMessage(i18n.unsupportedContent));
		return (
			<>
				<MacroExperienceFailure
					name={experienceName}
					contentId={contentId}
					extensionKey={extensionKey}
					mode={mode}
					attributes={attributes}
					error={unsupportedContentError}
				/>
				<Box xcss={styles.error} testId="pagetree-error-unsupported">
					<FormattedMessage {...i18n.unsupportedContent} />
				</Box>
			</>
		);
	}

	if (loading) {
		return (
			<Box testId="pagetree-loading">
				<Inline alignBlock="center" space="space.100">
					<Spinner size="medium" />
					<Text>{formatMessage(i18n.loading)}</Text>
				</Inline>
			</Box>
		);
	}

	if (error) {
		const pageTreeError = new Error(error);
		return (
			<>
				<MacroExperienceFailure
					name={experienceName}
					contentId={contentId}
					extensionKey={extensionKey}
					mode={mode}
					attributes={attributes}
					error={pageTreeError}
				/>
				<ErrorDisplay error={pageTreeError} />
			</>
		);
	}

	// Only a genuinely unresolvable root (e.g. a title/@home/@parent root that
	// points at nothing) is a "not found" failure. A root that resolves but has
	// no children is a valid, empty tree — matching the legacy backend, which
	// renders an empty tree in that case rather than surfacing an error.
	if (!rootResolved) {
		const rootNotFoundError = new Error(formatMessage(i18n.rootNotFound));
		return (
			<>
				<MacroExperienceFailure
					name={experienceName}
					contentId={contentId}
					extensionKey={extensionKey}
					mode={mode}
					attributes={attributes}
					error={rootNotFoundError}
				/>
				<Box xcss={styles.error} testId="pagetree-error-root-not-found">
					<FormattedMessage {...i18n.rootNotFound} />
				</Box>
			</>
		);
	}

	// Root resolved. `nodes` may still be empty (page has no children) — render
	// an empty tree and report success, rather than a failure.
	const treeNodes = nodes ?? [];

	return (
		<>
			<MacroExperienceSuccess
				name={experienceName}
				contentId={contentId}
				extensionKey={extensionKey}
				mode={mode}
				attributes={attributes}
			/>
			<Box
				testId="pagetree-macro"
				data-fabric-macro={macroDefaultProps?.attributes?.macroId}
				data-macro-body=""
				data-macro-parameters={jsonParams}
			>
				{(showExpandCollapseAll || showSearchBox) && (
					<Stack xcss={styles.controls}>
						{showSearchBox && (
							<Form onSubmit={handleSearchSubmit}>
								<Inline alignBlock="center">
									<Box xcss={styles.searchInput}>
										<Textfield
											value={searchQuery}
											onChange={handleSearchChange}
											testId="pagetree-search"
											aria-label={formatMessage(i18n.searchPlaceholder)}
										/>
									</Box>
									<Button appearance="default" type="submit" testId="pagetree-search-button">
										{formatMessage(i18n.searchButton)}
									</Button>
								</Inline>
							</Form>
						)}
						{showExpandCollapseAll && (
							<Inline space="space.200">
								<Pressable
									xcss={styles.linkButton}
									onClick={handleExpandAll}
									testId="pagetree-expand-all"
								>
									{formatMessage(i18n.expandAll)}
								</Pressable>
								<Pressable
									xcss={styles.linkButton}
									onClick={handleCollapseAll}
									testId="pagetree-collapse-all"
								>
									{formatMessage(i18n.collapseAll)}
								</Pressable>
							</Inline>
						)}
					</Stack>
				)}

				<Stack key={`${expandAllCounter}-${collapseAllCounter}`}>
					{treeNodes.map((treeNode) => (
						<PageTreeNodeComponent
							key={treeNode.id}
							node={treeNode}
							showExcerpt={showExcerpt}
							startDepth={effectiveStartDepth}
							currentDepth={0}
							currentPageId={macroDefaultProps.contentId}
							spaceKey={spaceKey}
							spaceId={macroDefaultProps.spaceId}
							sort={sort}
							reverse={reverse}
							ancestorIds={ancestorIds}
							RendererWrapper={RendererWrapper}
							excerptMap={excerptMap}
							enqueueExcerptIds={enqueueExcerptIds}
							loadedViaAutoExpand
						/>
					))}
				</Stack>
			</Box>
		</>
	);
};

export const PageTreeMacroRenderer = (props: PageTreeMacroRendererProps): React.JSX.Element => {
	return (
		<GenericErrorBoundary
			renderOnError={(_error: any) => {
				return <FormattedMessage {...i18n.error} />;
			}}
			attribution={Attribution.PAGE_EXTENSIONS}
		>
			<PageTreeMacroRendererComponent {...props} />
		</GenericErrorBoundary>
	);
};
